import React, { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { Row, Col, Spinner, Table } from "reactstrap"
import api from "API/Api"
import PageLoader from "./../../../assets/loading/pageLoad1.gif"

// ── Blank row template ──────────────────────────────────────────────────────
const BLANK = () => ({
  serialNo: "", producerNum: "", producerId: null, producerName: "",
  producerStatus: "idle",   // idle | loading | found | not_found
  milkType: "3", qtyLtrs: "", purchaseFat: "", purchaseSnf: "",
  purchaseClr: "", ratePerLtr: "", incentive: "0",
})

// ── Small helpers ────────────────────────────────────────────────────────────
const selEl = (val, list, onChange, ph, loading, lk) => (
  <select className="form-control form-control-sm" value={val}
    onChange={e => onChange(e.target.value)} style={{ minWidth: "120px" }}>
    <option value="">{loading ? "Loading…" : ph}</option>
    {list.map((it, i) => (
      <option key={i} value={it.id}>
        {it[lk] || it.name || it.routeName || it.centerName || it.collectionCenter}
      </option>
    ))}
  </select>
)

const radioEl = (name, value, current, setter, label) => (
  <label style={{ marginRight: 12, cursor: "pointer", fontSize: 13 }}>
    <input type="radio" name={name} checked={current === value}
      onChange={() => setter(value)} style={{ marginRight: 4 }} />
    {label}
  </label>
)

const fLabel = txt => (
  <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
    {txt} *
  </label>
)

// ── Extract array from API response ─────────────────────────────────────────
const arr = r => { const d = r?.data?.data; return Array.isArray(d) ? d : d?.data || [] }

// ── Main component ───────────────────────────────────────────────────────────
const AddVlccPurchase = () => {
  const [pageLoad, setPageLoad] = useState(true)

  // Cascade lists
  const [companies,   setCompanies]   = useState([])
  const [plants,      setPlants]      = useState([])
  const [chillings,   setChillings]   = useState([])
  const [routes,      setRoutes]      = useState([])
  const [collections, setCollections] = useState([])
  const [producers,   setProducers]   = useState([])

  // Cascade selections
  const [selCompany,    setSelCompany]    = useState("")
  const [selPlant,      setSelPlant]      = useState("")
  const [selCC,         setSelCC]         = useState("")
  const [selRoute,      setSelRoute]      = useState("")
  const [selCollection, setSelCollection] = useState("")

  // Loading flags
  const [plantsLoad, setPlantsLoad] = useState(false)
  const [ccLoad,     setCcLoad]     = useState(false)
  const [routeLoad,  setRouteLoad]  = useState(false)
  const [cctrLoad,   setCctrLoad]   = useState(false)
  const [prodLoad,   setProdLoad]   = useState(false)

  // Session fields
  const today = new Date().toISOString().split("T")[0]
  const [purchaseDate, setPurchaseDate] = useState(today)
  const [shift,        setShift]        = useState("0")
  const [entryMode,    setEntryMode]    = useState("SNF")
  const [useShortNum,  setUseShortNum]  = useState("No")

  // Rows + save state
  const [rows,   setRows]   = useState([])
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  // ── All required header fields filled? ──────────────────────────────────────
  const tableReady = !!(selPlant && selCC && selRoute && selCollection && purchaseDate)

  // ── Boot ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r  = await api.get("companies")
        const cos = arr(r)
        setCompanies(cos)
        if (cos.length <= 1) setPlants(arr(await api.get("plants")))
      } catch(e) {}
      setPageLoad(false)
    })()
  }, [])

  // Reset rows whenever required header fields change (stale rows are invalid)
  useEffect(() => { setRows([]) }, [selCC, selRoute, selCollection, purchaseDate, shift])

  // ── Cascade handlers ─────────────────────────────────────────────────────────
  const onCompany = async v => {
    setSelCompany(v); setSelPlant(""); setSelCC(""); setSelRoute(""); setSelCollection("")
    setChillings([]); setRoutes([]); setCollections([]); setProducers([])
    try {
      setPlants(arr(await api.get(v ? "plants-by-company/" + v : "plants")))
    } catch(e) {}
  }
  const onPlant = async v => {
    setSelPlant(v); setSelCC(""); setSelRoute(""); setSelCollection("")
    setChillings([]); setRoutes([]); setCollections([]); setProducers([])
    if (!v) return
    setCcLoad(true)
    try { setChillings(arr(await api.get("chillings-by-plant/" + v))) } catch(e) {}
    setCcLoad(false)
  }
  const onCC = async v => {
    setSelCC(v); setSelRoute(""); setSelCollection("")
    setRoutes([]); setCollections([]); setProducers([])
    if (!v) return
    setRouteLoad(true)
    try { setRoutes(arr(await api.get("route-cc/" + v))) } catch(e) {}
    setRouteLoad(false)
  }
  const onRoute = async v => {
    setSelRoute(v); setSelCollection("")
    setCollections([]); setProducers([])
    if (!v) return
    setCctrLoad(true)
    try { setCollections(arr(await api.get("collection-center-route/" + v))) } catch(e) {}
    setCctrLoad(false)
  }
  const onCollection = async v => {
    setSelCollection(v); setProducers([])
    if (!v) return
    setProdLoad(true)
    try { setProducers(arr(await api.get("producer-center/" + v))) } catch(e) {}
    setProdLoad(false)
  }

  // ── Row management ────────────────────────────────────────────────────────────
  const addRow = () => {
    if (!tableReady) return
    setRows(prev => [...prev, BLANK()])
  }
  const removeRow = i => setRows(prev => prev.filter((_, idx) => idx !== i))
  const setRow    = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r))

  // ── Producer lookup (short number mode) ────────────────────────────────────
  const lookupProducer = useCallback(async (i, num) => {
    if (!num) { setRow(i, { producerName: "", producerId: null, producerStatus: "idle" }); return }
    setRow(i, { producerStatus: "loading" })
    try {
      const r = await api.get("lookup-producer-by-number", {
        params: { number: num, collectionCenter: selCollection, useShort: useShortNum === "Yes" ? 1 : 0 }
      })
      const p = r.data?.data
      setRow(i, {
        producerId:     p.id,
        producerName:   p.producerName,
        milkType:       String(p.milkType || 3),
        producerStatus: "found",
      })
    } catch(e) {
      setRow(i, { producerId: null, producerName: "Not Found", producerStatus: "not_found" })
    }
  }, [selCollection, useShortNum])

  // ── Rate auto-lookup on FAT/SNF blur ────────────────────────────────────────
  const lookupRate = useCallback(async (i, row) => {
    const fat = parseFloat(row.purchaseFat)
    const snf = parseFloat(entryMode === "SNF" ? row.purchaseSnf : row.purchaseClr)
    if (!fat || !snf || !selCollection) return
    if (row.ratePerLtr) return   // don't overwrite manually entered rate
    try {
      const r = await api.post("lookup-rate", {
        collectionCenter: selCollection,
        milkType: row.milkType || 3,
        fat, snf,
        qtyLtrs: row.qtyLtrs || 0,
      })
      if (r.data?.success && r.data.data?.ratePerLtr) {
        setRow(i, { ratePerLtr: r.data.data.ratePerLtr })
      }
    } catch(e) {}
  }, [selCollection, entryMode])

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!tableReady) return alert("Please fill all required header fields first.")
    if (!rows.length) return alert("Click 'Add New' to add at least one row.")

    // Validate rows
    const validRows = []
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]
      // Resolve producer ID
      const producerId = useShortNum === "Yes"
        ? r.producerId
        : (r.producer || null)   // dropdown sets r.producer = id
      if (!producerId) {
        alert(`Row ${i + 1}: Producer not resolved. ${useShortNum === "Yes" ? "Tab out of the Producer field to look up." : "Please select a producer."}`)
        return
      }
      if (!r.purchaseFat) { alert(`Row ${i + 1}: FAT is required.`); return }
      validRows.push({
        serialNo:     r.serialNo,
        producerId,
        milkType:     r.milkType,
        qtyLtrs:      r.qtyLtrs   || "0",
        purchaseFat:  r.purchaseFat,
        purchaseSnf:  entryMode === "SNF" ? (r.purchaseSnf || "0") : "0",
        purchaseClr:  entryMode === "CLR" ? (r.purchaseClr || "0") : "0",
        ratePerLtr:   r.ratePerLtr || "0",
        incentive:    r.incentive  || "0",
      })
    }
    if (!validRows.length) return alert("No valid rows to save.")

    setSaving(true); setResult(null)
    try {
      const r = await api.post("bulk-create-vlcc-purchases", {
        chillingCenter:   selCC,
        route:            selRoute,
        collectionCenter: selCollection,
        plant:            selPlant,
        purchaseDate,
        shift,
        entryMode,
        useShortNum,
        rows: validRows,
      })
      setResult({ success: r.data.success, message: r.data.data, saved: r.data.saved, failed: r.data.failed, errors: r.data.errors || [] })
      if (r.data.success && r.data.saved > 0) setRows([])
    } catch(e) {
      setResult({ success: false, message: e.response?.data?.data || "Save failed.", saved: 0, failed: 0, errors: [] })
    }
    setSaving(false)
  }

  if (pageLoad) return <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader">
          <Row>
            <Col md={6}><h4>Purchase Bulk Add <span style={{ color: "#dc3545", fontSize: 13, fontWeight: 400 }}>[* required]</span></h4></Col>
            <Col md={6} style={{ textAlign: "right" }}>
              <Link to="/vlcc-purchases" className="dashBtn"><i className="fas fa-chevron-left" /> <b>Back</b></Link>
            </Col>
          </Row>
        </div>

        <div className="dashContent">

          {/* ── Row 1: Cascade dropdowns ── */}
          <Row>
            {companies.length > 1 && (
              <Col md={2}>
                {fLabel("Company")}
                {selEl(selCompany, companies, onCompany, "Select Company", false, "name")}
              </Col>
            )}
            <Col md={companies.length > 1 ? 2 : 3}>
              {fLabel("Plant")}
              {selEl(selPlant, plants, onPlant, "Select Plant", plantsLoad, "name")}
            </Col>
            <Col md={companies.length > 1 ? 2 : 3}>
              {fLabel("Chilling Center")}
              {selEl(selCC, chillings, onCC, "Select CC", ccLoad, "name")}
            </Col>
            <Col md={companies.length > 1 ? 2 : 3}>
              {fLabel("Route")}
              {selEl(selRoute, routes, onRoute, "Select Route", routeLoad, "routeName")}
            </Col>
            <Col md={companies.length > 1 ? 2 : 3}>
              {fLabel("Collection Center")}
              {selEl(selCollection, collections, onCollection, "Select Center", cctrLoad, "centerName")}
            </Col>
          </Row>

          <div style={{ marginTop: 14 }} />

          {/* ── Row 2: Date / Shift / EntryMode / UseShortNum ── */}
          <Row>
            <Col md={3}>
              {fLabel("Date")}
              <input type="date" className="form-control form-control-sm"
                value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13, display: "block" }}>Shift *</label>
              <div style={{ display: "flex", alignItems: "center", height: 31 }}>
                {radioEl("shift", "0", shift, setShift, "AM")}
                {radioEl("shift", "1", shift, setShift, "PM")}
              </div>
            </Col>
            <Col md={3}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13, display: "block" }}>Entry Mode *</label>
              <div style={{ display: "flex", alignItems: "center", height: 31 }}>
                {radioEl("entryMode", "SNF", entryMode, setEntryMode, "SNF")}
                {radioEl("entryMode", "CLR", entryMode, setEntryMode, "CLR")}
              </div>
            </Col>
            <Col md={3}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13, display: "block" }}>Use Producer Short Number *</label>
              <div style={{ display: "flex", alignItems: "center", height: 31 }}>
                {radioEl("useShortNum", "Yes", useShortNum, setUseShortNum, "Yes")}
                {radioEl("useShortNum", "No",  useShortNum, setUseShortNum, "No")}
              </div>
            </Col>
          </Row>

          {/* ── Required fields notice + Add New button ── */}
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <button
              className={`btn btn-sm ${tableReady ? "btn-primary" : "btn-secondary"}`}
              onClick={addRow}
              disabled={!tableReady}
              title={tableReady ? "Add a new entry row" : "Fill all required header fields first (Plant → CC → Route → Collection Center → Date)"}
              style={{ opacity: tableReady ? 1 : 0.6 }}
            >
              <i className="fas fa-plus" style={{ marginRight: 5 }} />Add New
            </button>

            {!tableReady && (
              <span style={{ fontSize: 12, color: "#dc3545", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fas fa-exclamation-circle" />
                Please complete all required fields above (Plant, Chilling Center, Route, Collection Center, Date) to add entries.
              </span>
            )}

            {tableReady && rows.length > 0 && (
              <span style={{ fontSize: 12, color: "#6c757d" }}>
                {rows.length} row{rows.length !== 1 ? "s" : ""} — Tab out of Producer field to auto-resolve name &amp; rate
              </span>
            )}
          </div>

          {/* ── Result banner ── */}
          {result && (
            <div style={{
              background: result.success ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${result.success ? "#86efac" : "#fca5a5"}`,
              borderRadius: 6, padding: "8px 14px", marginTop: 12, fontSize: 13,
            }}>
              <strong>{result.message}</strong>
              {result.errors?.length > 0 && (
                <ul style={{ marginTop: 4, paddingLeft: 18, marginBottom: 0 }}>
                  {result.errors.map((e, i) => <li key={i} style={{ color: "#991b1b" }}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* ── Entry table — only when required fields filled AND rows exist ── */}
          {tableReady && rows.length > 0 && (
            <div className="table-responsive mt-3">
              <Table className="table mb-0 dairyTbl" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th style={{ width: 70 }}>Serial No</th>
                    <th>Producer {useShortNum === "Yes" ? <span style={{ color: "#6c757d", fontWeight: 400 }}>(Short No. → auto-lookup)</span> : ""}</th>
                    <th style={{ width: 90 }}>Milk Type</th>
                    <th style={{ width: 80 }}>Qty Ltrs</th>
                    <th style={{ width: 65 }}>FAT *</th>
                    <th style={{ width: 65 }}>{entryMode}</th>
                    <th style={{ width: 85 }}>Rate/Ltr</th>
                    <th style={{ width: 75 }}>Incentive</th>
                    <th style={{ width: 28 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {/* # */}
                      <td style={{ verticalAlign: "middle", textAlign: "center", fontWeight: 600 }}>{i + 1}</td>

                      {/* Serial No */}
                      <td>
                        <input type="text" className="form-control form-control-sm"
                          value={row.serialNo}
                          onChange={e => setRow(i, { serialNo: e.target.value })} />
                      </td>

                      {/* Producer */}
                      <td>
                        {useShortNum === "Yes" ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ width: 90 }}
                              placeholder="No."
                              value={row.producerNum}
                              onChange={e => setRow(i, { producerNum: e.target.value, producerId: null, producerName: "", producerStatus: "idle" })}
                              onBlur={e => lookupProducer(i, e.target.value.trim())}
                            />
                            <span style={{
                              fontSize: 12, minWidth: 100,
                              color: row.producerStatus === "not_found" ? "#dc3545"
                                   : row.producerStatus === "found"     ? "#16a34a"
                                   : "#6c757d",
                              fontWeight: row.producerStatus === "found" ? 600 : 400,
                            }}>
                              {row.producerStatus === "loading" && <Spinner size="sm" />}
                              {row.producerStatus !== "loading" && (row.producerName || (row.producerStatus === "idle" ? "—" : ""))}
                            </span>
                          </div>
                        ) : (
                          <select
                            className="form-control form-control-sm"
                            value={row.producer || ""}
                            onChange={e => setRow(i, { producer: e.target.value })}
                          >
                            <option value="">{prodLoad ? "Loading…" : "Select Producer"}</option>
                            {producers.map((p, j) => (
                              <option key={j} value={p.id}>
                                {p.producerName || p.name}{p.producerCode ? ` (${p.producerCode})` : ""}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      {/* Milk Type */}
                      <td>
                        <select className="form-control form-control-sm"
                          value={row.milkType}
                          onChange={e => setRow(i, { milkType: e.target.value })}>
                          <option value="1">Cow</option>
                          <option value="2">Buffalo</option>
                          <option value="3">Common</option>
                        </select>
                      </td>

                      {/* Qty */}
                      <td>
                        <input type="number" step="0.001" min="0"
                          className="form-control form-control-sm"
                          value={row.qtyLtrs}
                          onChange={e => setRow(i, { qtyLtrs: e.target.value })} />
                      </td>

                      {/* FAT */}
                      <td>
                        <input type="number" step="0.01" min="0" max="15"
                          className="form-control form-control-sm"
                          value={row.purchaseFat}
                          onChange={e => setRow(i, { purchaseFat: e.target.value, ratePerLtr: "" })}
                          onBlur={() => lookupRate(i, { ...row })} />
                      </td>

                      {/* SNF or CLR */}
                      <td>
                        {entryMode === "SNF"
                          ? <input type="number" step="0.01" min="0" max="15"
                              className="form-control form-control-sm"
                              value={row.purchaseSnf}
                              onChange={e => setRow(i, { purchaseSnf: e.target.value, ratePerLtr: "" })}
                              onBlur={() => lookupRate(i, { ...row })} />
                          : <input type="number" step="0.01" min="0" max="50"
                              className="form-control form-control-sm"
                              value={row.purchaseClr}
                              onChange={e => setRow(i, { purchaseClr: e.target.value, ratePerLtr: "" })}
                              onBlur={() => lookupRate(i, { ...row })} />
                        }
                      </td>

                      {/* Rate/Ltr — auto-filled or manual */}
                      <td>
                        <input type="number" step="0.01" min="0"
                          className="form-control form-control-sm"
                          style={{ background: row.ratePerLtr ? "#f0fdf4" : undefined }}
                          value={row.ratePerLtr}
                          onChange={e => setRow(i, { ratePerLtr: e.target.value })}
                          placeholder="auto" />
                      </td>

                      {/* Incentive */}
                      <td>
                        <input type="number" step="0.01" min="0"
                          className="form-control form-control-sm"
                          value={row.incentive}
                          onChange={e => setRow(i, { incentive: e.target.value })} />
                      </td>

                      {/* Remove */}
                      <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                        <button onClick={() => removeRow(i)} title="Remove row"
                          style={{ background: "#dc3545", color: "#fff", border: "none", borderRadius: 4, width: 24, height: 24, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-times" style={{ fontSize: 10 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* ── Bottom actions ── */}
          {tableReady && rows.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="btn btn-success" onClick={handleSave} disabled={saving}>
                {saving ? <><Spinner size="sm" /> Saving…</> : <><i className="fas fa-save" /> Save Changes</>}
              </button>
              <Link to="/vlcc-purchases" className="btn btn-danger">
                <i className="fas fa-times" /> Close
              </Link>
            </div>
          )}

        </div>
      </div>
    </>
  )
}

export default AddVlccPurchase
